import React from 'react';
import {
  EnumDraftsState,
  FieldErrorsState,
  UseWorkflowInputEditorParams,
  UseWorkflowInputEditorResult,
  WorkflowInputField,
} from './workflow-input-editor.types';
import {
  fieldsToSchema,
  randomId,
  schemaToFields,
  validateFields,
} from './workflow-input-editor.utils';

export const useWorkflowInputEditor = (
  params: UseWorkflowInputEditorParams
): UseWorkflowInputEditorResult => {
  const { value, onChange, onValidationChange } = params;
  const [fields, setFields] = React.useState<WorkflowInputField[]>(() =>
    schemaToFields(value)
  );
  const [errors, setErrors] = React.useState<string[]>([]);
  const [fieldErrors, setFieldErrors] = React.useState<FieldErrorsState>({});
  const [enumDrafts, setEnumDrafts] = React.useState<EnumDraftsState>({});
  const lastSchemaSig = React.useRef<string | null>(null);

  React.useEffect(() => {
    const nextSig = JSON.stringify(value ?? {});
    if (lastSchemaSig.current === nextSig) return;
    lastSchemaSig.current = nextSig;
    setFields(schemaToFields(value));
  }, [value]);

  React.useEffect(() => {
    const validation = validateFields(fields);
    setErrors(validation.globalErrors);
    setFieldErrors(validation.fieldErrors);
    const schema = fieldsToSchema(fields);
    const schemaSig = JSON.stringify(schema);
    lastSchemaSig.current = schemaSig;
    onChange?.(schema);
    onValidationChange?.([
      ...validation.globalErrors,
      ...Object.values(validation.fieldErrors).flat(),
    ]);
  }, [fields, onChange, onValidationChange]);

  const setEnumDraft = React.useCallback((fieldId: string, value: string) => {
    setEnumDrafts((prev) => ({ ...prev, [fieldId]: value }));
  }, []);

  const addField = React.useCallback(() => {
    setFields((prev) => [
      ...prev,
      {
        id: randomId(),
        name: `campo_${prev.length + 1}`,
        type: 'string',
        required: false,
      },
    ]);
  }, []);

  const updateField = React.useCallback(
    (id: string, patch: Partial<WorkflowInputField>) => {
      setFields((prev) =>
        prev.map((f) => {
          if (f.id !== id) return f;
          const next: WorkflowInputField = { ...f, ...patch };
          if (patch.type && patch.type !== 'array') {
            next.itemType = undefined;
            next.minItems = undefined;
            next.maxItems = undefined;
          }
          if (patch.type && patch.type !== 'object') {
            next.subFields = undefined;
            next.allowAdditionalProps = undefined;
          }
          if (patch.type && patch.type !== 'string') {
            next.format = undefined;
            next.enumValues = undefined;
            next.minLength = undefined;
            next.maxLength = undefined;
          }
          if (
            patch.type &&
            (patch.type === 'boolean' || patch.type === 'string')
          ) {
            next.min = undefined;
            next.max = undefined;
          }
          return next;
        })
      );
    },
    []
  );

  const removeField = React.useCallback((id: string) => {
    setFields((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const addSubField = React.useCallback((parentId: string) => {
    setFields((prev) =>
      prev.map((f) => {
        if (f.id !== parentId) return f;
        const subFields = f.subFields || [];
        return {
          ...f,
          subFields: [
            ...subFields,
            {
              id: randomId(),
              name: `subcampo_${subFields.length + 1}`,
              type: 'string',
              required: false,
            },
          ],
        };
      })
    );
  }, []);

  const updateSubField = React.useCallback(
    (parentId: string, subId: string, patch: Partial<WorkflowInputField>) => {
      setFields((prev) =>
        prev.map((f) => {
          if (f.id !== parentId) return f;
          const subFields = (f.subFields || []).map((sf) => {
            if (sf.id !== subId) return sf;
            const next: WorkflowInputField = { ...sf, ...patch };
            if (patch.type && patch.type !== 'array') {
              next.itemType = undefined;
              next.minItems = undefined;
              next.maxItems = undefined;
            }
            if (patch.type && patch.type !== 'object') {
              next.subFields = undefined;
              next.allowAdditionalProps = undefined;
            }
            if (patch.type && patch.type !== 'string') {
              next.format = undefined;
              next.enumValues = undefined;
              next.minLength = undefined;
              next.maxLength = undefined;
            }
            if (
              patch.type &&
              (patch.type === 'boolean' || patch.type === 'string')
            ) {
              next.min = undefined;
              next.max = undefined;
            }
            return next;
          });
          return { ...f, subFields };
        })
      );
    },
    []
  );

  const removeSubField = React.useCallback(
    (parentId: string, subId: string) => {
      setFields((prev) =>
        prev.map((f) => {
          if (f.id !== parentId) return f;
          return {
            ...f,
            subFields: (f.subFields || []).filter((sf) => sf.id !== subId),
          };
        })
      );
    },
    []
  );

  const addEnumValue = React.useCallback(
    (fieldId: string) => {
      const draftValue = (enumDrafts[fieldId] || '').trim();
      if (!draftValue) return;
      setFields((prev) =>
        prev.map((f) => {
          if (f.id !== fieldId) return f;
          const existing = f.enumValues || [];
          if (existing.includes(draftValue)) return f;
          return { ...f, enumValues: [...existing, draftValue] };
        })
      );
      setEnumDrafts((prev) => ({ ...prev, [fieldId]: '' }));
    },
    [enumDrafts]
  );

  const removeEnumValue = React.useCallback(
    (fieldId: string, value: string) => {
      setFields((prev) =>
        prev.map((f) => {
          if (f.id !== fieldId) return f;
          return {
            ...f,
            enumValues: (f.enumValues || []).filter((v) => v !== value),
          };
        })
      );
    },
    []
  );

  return {
    fields,
    errors,
    fieldErrors,
    enumDrafts,
    setEnumDraft,
    addField,
    updateField,
    removeField,
    addSubField,
    updateSubField,
    removeSubField,
    addEnumValue,
    removeEnumValue,
  };
};
